import { http } from '@/lib/http';
import { saveBlob } from '@/lib/reconciliation/api';
import type { IDisruptionStatement, IInvoiceByDisruption, ILineEvidence, Invoice } from './types';

export async function listInvoices(): Promise<Invoice[]> {
  return http.get('v1/billing/invoices').json<Invoice[]>();
}

// Billing reports (docs/verticals/billing-reports.md §6.7) — `billing.view`, prices only.
// The `.csv` twins are generated by the BFF (BOM, `;`, comma decimals, D6); the console
// only fetches them with the session's token and hands the file to the browser.

const statementPath = (urn: string) => `v1/billing/disruptions/${encodeURIComponent(urn)}/statement`;
const byDisruptionPath = (urn: string) => `v1/billing/invoices/${encodeURIComponent(urn)}/by-disruption`;

export async function fetchStatement(disruptionUrn: string): Promise<IDisruptionStatement> {
  return http.get(statementPath(disruptionUrn)).json<IDisruptionStatement>();
}

export async function fetchInvoiceByDisruption(invoiceUrn: string): Promise<IInvoiceByDisruption> {
  return http.get(byDisruptionPath(invoiceUrn)).json<IInvoiceByDisruption>();
}

/** Seam 6.7: the BFF scopes a line to its disruption, so `disruptionUrn` is required (400 without it). */
export async function fetchLineEvidence(chargeUrn: string, disruptionUrn: string): Promise<ILineEvidence> {
  return http
    .get(`v1/billing/lines/${encodeURIComponent(chargeUrn)}/evidence`, { searchParams: { disruptionUrn } })
    .json<ILineEvidence>();
}

/** Fetch a `.csv` twin and save it under the filename the BFF stamped. */
async function downloadCsv(path: string, fallback: string): Promise<void> {
  const res = await http.get(path);
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1];
  saveBlob(await res.blob(), name ?? fallback);
}

export function downloadStatementCsv(disruptionUrn: string): Promise<void> {
  return downloadCsv(`${statementPath(disruptionUrn)}.csv`, 'estado-de-cuenta.csv');
}

export function downloadInvoiceCsv(invoiceUrn: string): Promise<void> {
  return downloadCsv(`${byDisruptionPath(invoiceUrn)}.csv`, 'factura-por-disrupcion.csv');
}
